import { UserRoles, WholesaleRoles } from '@modules/common/models/enums/UserRoles'
import { User } from '@modules/models'
import { storage } from '@services/storageService'
import { strings } from '@stores'
import intersectionBy from 'lodash/intersectionBy'
import { calculateUserRoles } from './calculate-user-roles'

export interface IPermissionsDict {
  cluster?: IPermissions
  store?: IPermissions
  zone?: IPermissions
}
export interface IPermissions {
  canEdit?: boolean
  canRead?: boolean
}

const userRolesWeight = [
  { name: UserRoles.ADMINISTRATOR, weight: 1 },
  { name: UserRoles.CENTRAL_MERCHANDISER_WHOLESALE, weight: 2 },
  { name: UserRoles.ZONE_AREA_MANAGER, weight: 3 },
  { name: UserRoles.WHOLESALE_SHOWROOM_ASSISTANT, weight: 4 },
  { name: UserRoles.EXTERNAL_CLIENT, weight: 5 },
  { name: UserRoles.CM_ADMIN, weight: 6 },
  { name: UserRoles.CENTRAL_MERCHANDISER_RETAIL, weight: 7 },
  { name: UserRoles.REGIONAL_MERCHANDISER, weight: 8 }
]

export const getHighestUser = (user?: any): Array<{ name: string; weight: number }> => {
  const userObj = user || storage.getItem<User>('user')
  return userObj ? intersectionBy(userRolesWeight, userObj.roles, 'name').sort() : []
}

export const getPermissionsByEntityAndRole = (entity: string): IPermissions => {
  const highestUser = getHighestUser()
  const defaultPermissions = { canEdit: false, canRead: false }

  if (highestUser.length) {
    let highestWeight = highestUser[0].weight
    let allRolesWithHighestWeight = highestUser.filter(
      role => role.weight === highestWeight
    )
    let permissions = null
    const highestRoleWithPermission = allRolesWithHighestWeight.find(role => {
      permissions = entityAndRoleWisePermisions[entity][role.name]
      return permissions
    })
    return highestRoleWithPermission ? permissions : defaultPermissions
  }

  return defaultPermissions
}

const entityAndRoleWisePermisions = {
  buyingSessions: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 1)
  },
  collections: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 1)
  },
  lookbooks: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 1)
  },
  assortments: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 1)
  },
  [`${strings.cluster}Assortments`]: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 1)
  },
  [`${strings.zone}Assortments`]: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 0, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 0, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 1, 1)
  },
  [`${strings.store}Assortments`]: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 0, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 0, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 1, 1)
  },
  settings: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 0)
  },
  collectionSettings: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 0)
  },
  sync: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 0)
  },
  activityCollection: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 1)
  },
  bet: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 1)
  },
  reports: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 1, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 1, 1)
  },
  buy: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 0, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 0, 1),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 1, 1)
  },
  seasonSync: {
    ...calculateUserRoles(UserRoles.ADMINISTRATOR, 1, 1),
    ...calculateUserRoles(UserRoles.CENTRAL_MERCHANDISER_RETAIL, 0, 0),
    ...calculateUserRoles(UserRoles.REGIONAL_MERCHANDISER, 0, 0)
  }
}

export const getRootRouteByRole = () => {
  const rolesByWeight = getHighestUser()
  let route = '/'

  if (rolesByWeight && rolesByWeight.length) {
    const highestRole: any = rolesByWeight[0].name
    if (
      [
        UserRoles.WHOLESALE_SHOWROOM_ASSISTANT,
        UserRoles.CM_ADMIN,
        UserRoles.CENTRAL_MERCHANDISER_RETAIL
      ].includes(highestRole)
    ) {
      route = '/showroom'
    } else if (Object.values(WholesaleRoles).includes(highestRole)) {
      route = '/wholesale'
    }
  }

  return route
}
